import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ToastContainer, toast } from 'react-toastify';

import WT from '@sscale/wtsdk';
import { ServiceChat } from 'services/ServiceChat';
import { off, on, publish } from 'services/PubSub';
import { resetStore as resetCelebrityStore, setStreamSettings } from 'store/reducers/CelebrityReducer';

import { setSessionError, setParticipants, setJoinModalType } from 'store/reducers/SessionReducer';
import celebrityAPI from 'services/celebrity';
import Video from '../Video/Video';
import JoinModal from './components/JoinModal/JoinModal';
import Rooms from './components/Rooms/Rooms';
import Player from '../Player/Player';
import SessionError from './components/SessionError/SessionError';
import Participants from './components/Participants/Participants';
import { ErrorListener, Logger, Publisher, Subscriber, SubscriberListener, SubscriberEvents } from '@sscale/cdnwebsdk';
import { getPublisherToken } from '../../services/cdn';
import Chat from '../Chat/Chat';
import InteractivityLayer from '../InteractivityLayer/InteractivityLayer';
import { CDN_PREVIEW_TEMPLATES, resolveCdnPreviewTemplateKey } from './cdnPreviewTemplates';
import PollManager from '../Polls/PollManager';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import './Celebrity-ordered.scss';
import PChat from '../../services/PChat';

const errorHandler = (error) => {
  console.log(error.name);
  console.log(error.code);
  console.log(error.message);
  console.log(error.originalMessage);
};
ErrorListener.onError(errorHandler);
Logger.setLevel(Logger.DEBUG);
// const publisherLogger = Logger.get('Publisher');
// publisherLogger.setLevel(Logger.DEBUG);

const CDN_URL = 'https://cdn.management.sceenic.co/v1/stream';

let audioContext;
let audioDestination;
const audioSources = {};
const mixedStream = new MediaStream();

function initAudioContextIfNeeded() {
  if (!audioDestination || !audioContext) {
    audioContext = new AudioContext();
    audioDestination = audioContext.createMediaStreamDestination();
  }
}
function prepareMixedStream({ videoTracks, audioTracks }) {
  if (videoTracks) {
    mixedStream.getVideoTracks().forEach((track) => {
      mixedStream.removeTrack(track);
    });
    videoTracks.forEach((track) => {
      mixedStream.addTrack(track);
    });
  }

  if (audioTracks) {
    mixedStream.getAudioTracks().forEach((track) => {
      mixedStream.removeTrack(track);
    });
    audioTracks.forEach((track) => {
      mixedStream.addTrack(track);
    });
  }
}

function getAudioStream({ includeMainVideo }) {
  const videoElementsNodeList = document.querySelectorAll('video[id^="participant-"]');
  const videoElements = Array.from(videoElementsNodeList);
  const player = document.getElementById('player');

  if (player && includeMainVideo) {
    videoElements.push(player);
  }

  videoElements.forEach((video) => {
    const stream = video.srcObject;
    if (!audioSources[video.id]) {
      initAudioContextIfNeeded();
      const audioSource =
        stream instanceof MediaStream
          ? audioContext.createMediaStreamSource(stream)
          : audioContext.createMediaElementSource(video);
      audioSource.connect(audioDestination);
      audioSources[video.id] = audioSource;
    }
  });

  return audioDestination.stream;
}

function prepareCanvas(ctx, canvas) {
  ctx.fillStyle = '#000000'; // Change this to the color you want
  ctx.beginPath();
  ctx.roundRect(0, 0, canvas.width, canvas.height, 8);
  ctx.fill();
}

function SessionV2({
  cdnPreviewTemplate = null,
  sessionStream,
  isControlled = false,
  isStarRequired = true,
  showMainVideo = true,
  showConference = true,
  className,
  showInteractivityBelowPreview = false,
}) {
  const dispatch = useDispatch();

  const {
    isCelebrity,
    isModerator,
    isCoHost,
    userName,
    constraints,
    eveKey,
    streamName,
    room,
    tokens,
    cdnPublisherToken,
  } = useSelector((state) => state.celebrity);
  const { participants, connectedClb } = useSelector((state) => state.session);

  const [roomHasCelebrity, setRoomCelebrityPresence] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publisher, setPublisher] = useState(null);
  const [isCdnMuted, setIsCdnMuted] = useState(false);

  const participantsRef = useRef(participants);
  const connectedClbRef = useRef(connectedClb);
  const makeCelebrityLocalRef = useRef();
  const roomHasCelebrityRef = useRef(roomHasCelebrity);

  useEffect(() => {
    if (isCelebrity !== null) {
      if (roomHasCelebrity) {
        return;
      }

      makeCelebrityLocalRef.current();
    }
  }, [roomHasCelebrity, isCelebrity]);

  useEffect(() => {
    const initChat = async () => {
      if (room && userName) {
        await PChat.init(room, userName, eveKey, tokens.chat);
        initAudioContextIfNeeded();
      }
    };

    initChat();
  }, [userName]);

  useEffect(() => {
    participantsRef.current = participants;
  }, [participants]);

  useEffect(() => {
    roomHasCelebrityRef.current = roomHasCelebrity;
  }, [roomHasCelebrity]);

  useEffect(() => {
    connectedClbRef.current = connectedClb;
  }, [connectedClb]);

  useEffect(() => {
    WT.ErrorsListeners.onSessionError((error) => {
      dispatch(setSessionError('Unexpected error. Unable to join the room. Log in again, please.'));
      makeCelebrityLocalRef.current();
    });

    WT.SessionListeners.onConnected(() => {
      connectedClbRef.current();

      let participants = [];

      WT.SessionListeners.onStreamCreated((params) => {
        const alreadyExists = participants.find((p) => p.participantId === params.participantId);

        if (alreadyExists) {
          const video = document.getElementById('participant-' + params.participantId);
          video.src = params.stream;
          // dispatch(setParticipants([...participants.filter(p => p.participantId !== params.participantId), { ...params }]))
        } else if (params.isCelebrity) {
          setRoomCelebrityPresence(params);
        } else {
          participants.push(params);
          dispatch(setParticipants([...participants]));
        }

        initAudioContextIfNeeded();
        const audioSource = audioContext.createMediaStreamSource(params.stream);
        audioSource.connect(audioDestination);
        if (publisher) {
          console.log(`replacing audio  track`);
          publisher.replaceTrack(audioDestination.stream.getAudioTracks()[0]);
        }
      });

      WT.ParticipantListeners.onParticipantLeft(({ participantId }) => {
        if (roomHasCelebrityRef?.current && roomHasCelebrityRef?.current?.participantId === participantId) {
          setRoomCelebrityPresence(null);
        } else {
          participants = [...participants.filter((p) => p.participantId !== participantId)];
          dispatch(setParticipants(participants.filter((p) => p.participantId !== participantId)));
        }
      });
    });

    WT.ParticipantListeners.onParticipantMediaStreamChanged(publish.bind(null, 'onParticipantMediaStreamChanged'));
    WT.ParticipantListeners.onParticipantSpeaking(publish.bind(null, 'onParticipantSpeaking'));
    WT.ParticipantListeners.onParticipantStopSpeaking(publish.bind(null, 'onParticipantStopSpeaking'));

    function onParticipantMediaStreamChanged({ participantId, mediaType, mediaState }) {
      if (publisher) {
        switch (mediaType) {
          case 'VIDEO':
            const videoTrack = participantsRef.current
              .find((p) => p.participantId === participantId)
              ?.stream?.getVideoTracks()[0];
            if (videoTrack) {
              publisher.replaceTrack(videoTrack);
            }
            break;
          case 'AUDIO':
            const audioTrack = participantsRef.current
              .find((p) => p.participantId === participantId)
              ?.stream?.getAudioTracks()[0];
            if (audioTrack) {
              publisher.replaceTrack(audioTrack);
            }
            break;
        }
      }
    }
    on('onParticipantMediaStreamChanged', onParticipantMediaStreamChanged);

    return () => {
      off('onParticipantMediaStreamChanged', onParticipantMediaStreamChanged);
    };
  }, []);

  useEffect(() => {
    if (isCelebrity && !isModerator) {
      async function startWTSession() {
        await WT.Session.connect(tokens.wt, isCoHost ? 'co-host' : 'host', constraints, { isCelebrity: !isCoHost });
      }

      startWTSession();
    }
  }, [isCelebrity, isCoHost]);

  useEffect(() => {
    if (!isCelebrity) {
      return;
    }

    const init = async () => {
      await ServiceChat.connect(`${room}-service`, userName, tokens.chat);

      if (!isModerator) {
        ServiceChat.on(ServiceChat.Events.MESSAGE, (message) => {
          if (message.content === ServiceChat.CUSTOM_EVENTS.USER_READY) {
            toast('User is ready on camera', {
              position: 'top-center',
              autoClose: 10000,
              hideProgressBar: false,
              theme: 'dark',
            });
          } else if (message.content === ServiceChat.CUSTOM_EVENTS.DROP_GUESTS) {
            participantsRef.current?.forEach((participant) => {
              if (!participant.isCelebrity && participant.participantName !== 'co-host') {
                WT.Participant.disconnectRemoteParticipant(participant?.participantId);
              }
            });
          }
        });
      }
    };

    init();
  }, [isCelebrity]);

  useEffect(() => {
    if (!isCelebrity || !(isModerator || isCoHost)) {
      return;
    }

    dispatch(setStreamSettings({ syncToken: tokens.sync }));

    let sub = new Subscriber(CDN_URL, tokens.cdn);
    sub.subscribe();
    let stream = new MediaStream();

    const onTrack = (track) => {
      stream.addTrack(track);

      if (stream.active) {
        if (isModerator) {
          const cdn = document.getElementById('cdn');
          cdn.style.display = 'block';
        }

        const cdnVideo = document.getElementById('cdn-sub');
        if (cdnVideo) {
          cdnVideo.srcObject = stream;
          cdnVideo.style.display = 'block';
        }
      }
    };
    SubscriberListener.on('track', onTrack);
    SubscriberListener.on(SubscriberEvents.UNPUBLISHED, () => {
      stream = new MediaStream();
      // resubscribe
      sub = new Subscriber(CDN_URL, tokens.cdn);
      sub.subscribe();

      const cdnVideo = document.getElementById('cdn-sub');
      if (cdnVideo) {
        cdnVideo.srcObject = null;
        cdnVideo.style.display = 'none';
      }

      if (isModerator) {
        const cdn = document.getElementById('cdn');
        cdn.style.display = 'none';
      }
    });
  }, [isCelebrity, isModerator, isCoHost]);

  makeCelebrityLocalRef.current = useCallback(() => {
    dispatch(setParticipants([]));
    navigator.mediaDevices.getUserMedia(constraints).then((device) =>
      setRoomCelebrityPresence({
        stream: device,
        participantId: '1',
        participantName: userName,
        streamConstraints: constraints,
        local: true,
        preview: true,
      }),
    );
  }, [userName, constraints]);

  const stopLocalStream = () => {
    const tracks = roomHasCelebrityRef?.current?.stream?.getTracks();

    if (tracks && tracks.length > 0) {
      tracks[0].stop();
    }
  };

  useEffect(() => {
    return () => {
      stopLocalStream();
    };
  }, []);

  useEffect(() => {
    dispatch(setJoinModalType('CELEBRITY_JOIN_DATA'));
  }, []);

  useEffect(() => {
    let CDN_PREVIEW_INTERVAL_ID = null;
    const canvas = document.getElementById('cdn-preview');

    if (!canvas) return;

    const templateKey = resolveCdnPreviewTemplateKey({ cdnPreviewTemplate });
    const template = CDN_PREVIEW_TEMPLATES[templateKey] || CDN_PREVIEW_TEMPLATES.default;
    const padding = template.padding;
    const videoWidth = template.videoWidth;
    const videoHeight = template.videoHeight;

    const videos = document.querySelectorAll('video[id^="participant-"]');
    template.initCanvas({ canvas, videosCount: videos.length, videoWidth, videoHeight, padding });

    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Fill the canvas
    prepareCanvas(ctx, canvas);

    const player = document.getElementById('player');

    let videosNumber = videos.length;

    const drawAvailableVideos = () => {
      const videos = document.querySelectorAll('video[id^="participant-"]');
      if (videos.length !== videosNumber && template.shouldResizeOnVideosCountChange) {
        template.resizeCanvas({ canvas, videosCount: videos.length, videoWidth, videoHeight, padding });
        prepareCanvas(ctx, canvas);
        videosNumber = videos.length;
      }

      template.drawBackground({ ctx, canvas, player, padding, videoWidth, videoHeight });

      videos.forEach((video, index) => {
        if (video && video.paused) {
          try {
            const maybePromise = video.play();
            if (maybePromise && typeof maybePromise.catch === 'function') {
              maybePromise.catch(() => {});
            }
          } catch (e) {
            // ignore
          }
        }

        if (!video || video.readyState < 2 || video.videoWidth === 0 || video.videoHeight === 0) {
          return;
        }

        const { x, y } = template.getVideoPosition({ index, padding, videoWidth, videoHeight });
        ctx.drawImage(video, x, y, videoWidth, videoHeight);
        template.afterDrawVideo({ ctx, canvas, index, padding, videoWidth, videoHeight, isStarRequired });
      });
      // player.requestVideoFrameCallback(drawAvailableVideos); // stopped working when video is paused
      // requestAnimationFrame(drawAvailableVideos); // stopped working when tab is inactive
    };

    // player.requestVideoFrameCallback(drawAvailableVideos);
    // requestAnimationFrame(drawAvailableVideos);

    // create a loop with 60 fps
    const interval = 1000 / 30;
    CDN_PREVIEW_INTERVAL_ID = setInterval(drawAvailableVideos, interval);

    return () => {
      if (CDN_PREVIEW_INTERVAL_ID) {
        clearInterval(CDN_PREVIEW_INTERVAL_ID);
      }
    };
  }, [cdnPreviewTemplate, isStarRequired]);

  function logout() {
    WT.Session.disconnect();

    stopLocalStream();

    celebrityAPI.logout();

    setRoomCelebrityPresence(null);
    // setRoomsList([]);
    dispatch(setParticipants([]));
    dispatch(resetCelebrityStore());

    dispatch(setJoinModalType('CELEBRITY_JOIN_DATA'));
  }

  function handleDropGuests() {
    ServiceChat.publish(ServiceChat.CUSTOM_EVENTS.DROP_GUESTS);
  }

  function handleMute() {
    console.log('handleMute', isCdnMuted, publisher);
    if (!isCdnMuted) {
      console.log('mute');
      if (isModerator || isCoHost) {
        const cdnVideo = document.getElementById('cdn-sub');
        if (cdnVideo) {
          cdnVideo.muted = true;
        }
      } else {
        publisher.muteAudio();
      }

      setIsCdnMuted(true);
    } else {
      console.log('unmute');
      if (isModerator || isCoHost) {
        const cdnVideo = document.getElementById('cdn-sub');
        if (cdnVideo) {
          cdnVideo.muted = false;
        }
      } else {
        publisher.unmuteAudio();
      }
      setIsCdnMuted(false);
    }
  }

  async function startCdnPublishing() {
    if (isPublishing) {
      publisher.unpublish();
      dispatch(setStreamSettings({ syncToken: '' }));
      setIsPublishing(false);
      // window.location.reload();
      return;
    }
    try {
      const token = await getPublisherToken(streamName, cdnPublisherToken);
      const canvas = document.getElementById('cdn-preview');
      const stream = canvas.captureStream();

      const templateKey = resolveCdnPreviewTemplateKey({ cdnPreviewTemplate });
      const template = CDN_PREVIEW_TEMPLATES[templateKey] || CDN_PREVIEW_TEMPLATES.default;
      const audioStream = getAudioStream({ includeMainVideo: !!template.includeMainVideo });

      const videoTracks = stream.getVideoTracks();
      const audioTracks = audioStream.getAudioTracks();
      prepareMixedStream({ videoTracks, audioTracks });

      const startPublishing = (publisher) => {
        publisher.publish(mixedStream, 6000000, true);
        window.startRTMP = (CDN_URL, bitrate) => {
          publisher.exportToRtmp(CDN_URL, bitrate);
        };
      };
      if (!publisher) {
        const publisher = new Publisher(CDN_URL, token);
        setPublisher(publisher);
        startPublishing(publisher);
      } else {
        startPublishing(publisher);
      }

      // collecting stats on publisher side causing some stuttering in canvas loop
      // publisher.startCollectingStats();
      // setTimeout(() => {
      //   publisher.stopCollectingStats();
      // }, 10000);

      dispatch(setStreamSettings({ syncToken: tokens.sync }));

      setIsPublishing(true);
    } catch (error) {
      console.error('Error getting publisher token', error);
    }
  }

  return (
    <div className={`session-container is-celebrity-page${className ? ` ${className}` : ''}`}>
      <div className="s-body">
        <div className="s-celebrity-container">
          {showMainVideo && (
            <div className="s-celebrity-video">
              <Video participant={roomHasCelebrity} isControlled={isControlled} isCelebrity={true} />
            </div>
          )}
          {!showMainVideo && roomHasCelebrity && (
            <div style={{ position: 'absolute', left: -99999, top: -99999, width: 1, height: 1, overflow: 'hidden' }}>
              <Video participant={roomHasCelebrity} isControlled={isControlled} isCelebrity={true} />
            </div>
          )}
          <button className="s-logout" onClick={logout} title={'Log out'} />
          {!isCoHost && (
            <div className="s-celebrity-controls" id="cdn" style={{ display: isModerator ? 'none' : 'block' }}>
              {isModerator ? (
                <>
                  <video
                    id="cdn-sub"
                    className="cdn-sub-video"
                    autoPlay
                    playsInline
                    style={{ display: 'none' }}
                  ></video>
                  <button className="button room-next-button" onClick={handleMute}>
                    {isCdnMuted ? 'Unmute' : 'Mute'}
                  </button>
                  <button className="button room-next-button" onClick={handleDropGuests} style={{ marginTop: 12 }}>
                    Drop all guests
                  </button>
                </>
              ) : (
                <>
                  <canvas className="canvas-cdn-preview" id="cdn-preview" width="1280" height="720" />
                  {showInteractivityBelowPreview && userName && <InteractivityLayer />}
                  <button className="button room-next-button" onClick={startCdnPublishing}>
                    {isPublishing ? 'Stop' : 'Start'} Streaming
                  </button>
                  {/*<button className="button" onClick={handleMute}>*/}
                  {/*  Mute*/}
                  {/*</button>*/}
                </>
              )}
            </div>
          )}
        </div>

        {showConference && (
          <div className="s-activity-container">
            <div className="s-participants">
              <Participants isControlled={isControlled} />
            </div>

            <div className="s-player-container">
              <Player isLoggedIn={isCelebrity} isSyncRequired={true} componentStream={sessionStream} />
              {userName && <InteractivityLayer />}
            </div>
          </div>
        )}

        <div className="tools">
          <Tabs>
            <TabList>
              {isModerator && <Tab>Queue Manager</Tab>}
              <Tab>Chat</Tab>
              <Tab>Polls</Tab>
            </TabList>

            {isModerator && (
              <TabPanel>
                <Rooms key={userName} isLoggedIn={isCelebrity} makeCelebrityLocal={makeCelebrityLocalRef.current} />
              </TabPanel>
            )}
            <TabPanel
            // forceRender={true}
            >
              <Chat userId={userName} />
            </TabPanel>
            <TabPanel>
              <PollManager />
            </TabPanel>
          </Tabs>
          {/*<Rooms key={userName} isLoggedIn={isCelebrity} makeCelebrityLocal={makeCelebrityLocalRef.current} />*/}
          {/*<PollManager />*/}
        </div>

        <JoinModal />

        <SessionError />

        <ToastContainer />
      </div>
    </div>
  );
}

export default SessionV2;
